import { type FeatureDataResponse, type FeatureSchema, SchemaDefinitionType } from '@mynt/backoffice-api-spec/features'
import type { FeatureListItem, Field } from '../types'
import type { FeatureData } from './types'
import { FieldDataType } from './types'

export const mapFeatureSchemaDtoToFeatureListItem = (featureSchemaDto: FeatureSchema): FeatureListItem => {
  return {
    id: featureSchemaDto.featureId,
    name: featureSchemaDto.name,
  }
}

export const mapFeatureSchemaResponseToFeatureData = (featureSchemaResponse: FeatureSchema): FeatureData => {
  const fields = featureSchemaResponse.schema.map((fieldSchema): Field => {
    switch (fieldSchema.type) {
      case SchemaDefinitionType.BOOLEAN:
        return {
          name: fieldSchema.name,
          type: FieldDataType.BOOLEAN,
          value: false,
          required: fieldSchema.required,
        }
      case SchemaDefinitionType.INT_RANGE:
        return {
          name: fieldSchema.name,
          type: FieldDataType.INTEGER,
          value: 0,
          required: fieldSchema.required,
          min: fieldSchema.min,
          max: fieldSchema.max,
        }
      case SchemaDefinitionType.ENUM:
        return {
          name: fieldSchema.name,
          type: FieldDataType.ENUM,
          value: fieldSchema.allowedValues[0],
          required: Boolean(fieldSchema.required),
          allowedValues: fieldSchema.allowedValues,
        }
      default: {
        throw new Error(`Unsupported field type: ${(fieldSchema as any).type}`)
      }
    }
  })

  return {
    id: featureSchemaResponse.featureId,
    name: featureSchemaResponse.name,
    fields,
  }
}

interface FeatureAndSchemaMapperData {
  featureDataResponse: FeatureDataResponse
  featureSchemaResponse: FeatureSchema
}

export const mapFeatureDataAndSchemaResponseToFeatureData = ({
  featureDataResponse,
  featureSchemaResponse,
}: FeatureAndSchemaMapperData): FeatureData => {
  const fields: Field[] = featureSchemaResponse.schema.map((schemaField) => {
    const dataItem = featureDataResponse.data.find((field) => field.name === schemaField.name)

    if (!dataItem) {
      throw new Error(`Field data for field: ${schemaField.name}`)
    }

    if (dataItem.type === FieldDataType.BOOLEAN && schemaField.type === SchemaDefinitionType.BOOLEAN) {
      return {
        name: schemaField.name,
        type: FieldDataType.BOOLEAN,
        value: Boolean(dataItem.value),
        required: !!schemaField.required,
      }
    }

    if (dataItem.type === FieldDataType.INTEGER && schemaField.type === SchemaDefinitionType.INT_RANGE) {
      return {
        name: schemaField.name,
        type: FieldDataType.INTEGER,
        value: dataItem.value === undefined ? null : dataItem.value,
        required: schemaField.required,
        min: schemaField.min,
        max: schemaField.max,
      }
    }

    if (dataItem.type === FieldDataType.ENUM && schemaField.type === SchemaDefinitionType.ENUM) {
      return {
        name: schemaField.name,
        type: FieldDataType.ENUM,
        value: dataItem.value,
        required: !!schemaField.required,
        allowedValues: schemaField.allowedValues,
      }
    }

    console.error({ dataItem, schemaField })

    throw new Error(`Unsupported schema field type: ${schemaField.type}`)
  })

  return {
    id: featureDataResponse.id,
    name: featureSchemaResponse.name,
    fields,
  }
}

export const mapFeatureAndFormFieldsToFeatureRequest = (feature: FeatureData, formValues: Record<string, unknown>) => {
  return feature.fields.map((field) => {
    const matchingFormValue = formValues[field.name]

    return {
      name: field.name,
      type: field.type,
      value: matchingFormValue,
    }
  })
}
